import { Box, Typography, LinearProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DoneIcon from '@mui/icons-material/Done';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import {
  GetImportResponse,
  useGetFeatureAnalysisQuery,
  useGetImportDocumentQuery,
  useUploadFeatureAnalysesMutation,
  useUploadMutation,
} from 'src/redux/services/imports';
const fileTypes = ['CSV'];

export interface DragAndDropFilesProps {
  setValidatingFile: React.Dispatch<React.SetStateAction<boolean>>;
  setValidatedFileID: React.Dispatch<React.SetStateAction<string | undefined>>;
  appkey: string;
  isFeatureAnalyses: boolean;
}

const DragAndDropFiles: React.FC<DragAndDropFilesProps> = ({ setValidatingFile, appkey, setValidatedFileID, isFeatureAnalyses }) => {
  const [upload, { data, error }] = useUploadMutation();
  const [uploadFeatureAnalyses, { data: featureAnalysesData, error: errorFeatureAnalyses }] = useUploadFeatureAnalysesMutation();

  const [dataToShow, setDataToShow] = useState();
  const [previousAppKey, setPreviouAppKey] = useState<string>();
  const [file, setFile] = useState<File | undefined>();

  const [documentData, setDocumentData] = useState<GetImportResponse>();
  const { data: importData } = useGetImportDocumentQuery(data?.import_id, {
    pollingInterval: 3000,
    skip: !data,
  });

  const { data: documentAnalysesData } = useGetFeatureAnalysisQuery(
    { id: featureAnalysesData?.import_id },
    {
      pollingInterval: 3000,
      skip: !featureAnalysesData,
    }
  );
  const handleChange = (file: File[]) => {
    if (isFeatureAnalyses) {
      uploadFeatureAnalyses({ csvfile: file, appkey: appkey });
    } else {
      upload({ csvfile: file, appkey: appkey });
    }

    setFile(file[0]);
  };

  useEffect(() => {
    if (data || featureAnalysesData) {
      if (isFeatureAnalyses) setDataToShow(featureAnalysesData);
      else setDataToShow(data);
      setPreviouAppKey(appkey);
    }
  }, [data, featureAnalysesData]);
  useEffect(() => {
    if (appkey && previousAppKey && appkey !== previousAppKey) {
      setDocumentData(undefined);
      setDataToShow(undefined);
    }
  }, [appkey]);

  useEffect(() => {
    if (isFeatureAnalyses) {
      setDocumentData(documentAnalysesData);
    } else {
      setDocumentData(importData);
    }
  }, [documentAnalysesData, importData]);
  useEffect(() => {
    if (documentData?.item.import_status === 'VALIDATED') {
      setValidatingFile(false);

      setValidatedFileID(documentData.item.key);
    }
  }, [documentData]);

  const genericError = error || (errorFeatureAnalyses as any);
  return (
    <div style={{ marginTop: 20, cursor: 'pointer' }}>
      {!dataToShow && (
        <FileUploader multiple={true} handleChange={handleChange} name="upload-file" types={fileTypes} label="drag and drop files here or browse">
          <div
            style={{
              width: '100%',
              height: '200px',
              display: 'flex',
              paddingTop: 30,
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              textAlign: 'center',
              border: '1px dotted #D1D5DB',
              borderRadius: '8px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: '10px',
                paddingTop: 10,
                paddingBottom: 10,
                paddingLeft: 20,
                paddingRight: 20,
                color: 'white',
                backgroundColor: '#3B873E',

                flexDirection: 'column',
                justifyContent: 'center',
                marginBottom: '80px',
              }}
            >
              <DescriptionOutlinedIcon />
              <div>
                <Typography style={{ fontSize: 9, marginTop: 3 }}>CSV</Typography>
              </div>
            </div>

            <Typography
              style={{
                position: 'absolute',
                display: 'inline',
                marginTop: 50,
                width: 215,
              }}
              color="text.secondary"
            >
              Drag your files here to upload or browse
              <span
                color="primary"
                style={{
                  display: 'inline',
                  cursor: 'pointer',
                  marginLeft: 5,
                  color: '#151515',
                }}
              >
                your files
              </span>
            </Typography>
          </div>
        </FileUploader>
      )}
      {genericError?.data && (
        <Typography color="red" style={{ marginTop: 10 }} data-testid="generic-error">
          {Object.keys(genericError.data.violations).map((e, i) => (
            <span key={i}>{genericError.data.violations[e]}</span>
          ))}
        </Typography>
      )}
      {dataToShow && (
        <div
          data-testid="import-data"
          style={{
            backgroundColor: '#F5F5F5',
            height: 88,
            padding: '16px 24px 0px 24px',
          }}
        >
          <Typography variant="h5" style={{ marginBottom: 15 }}>
            {documentData?.item.import_status === 'VALIDATING' && <DescriptionOutlinedIcon fontSize="large" sx={{ marginBottom: '-10px' }} />}
            {documentData?.item.import_status === 'VALIDATED' && <DoneIcon fontSize="large" color="success" sx={{ marginBottom: '-10px' }} />}
            {documentData?.item.import_status === 'ERROR' && <PriorityHighIcon fontSize="large" color="error" sx={{ marginBottom: '-10px' }} />}
            <span style={{ marginLeft: 10 }}>{file?.name}</span>
          </Typography>
          {documentData?.item.import_status === 'VALIDATING' && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                value={documentData.item.number_of_processed_rows ? (documentData.item.number_of_processed_rows * 100) / documentData.item.number_of_rows : 0}
              />
            </Box>
          )}
          {documentData?.item.import_status === 'VALIDATED' && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress variant="determinate" color="success" value={100} />
            </Box>
          )}
          {documentData?.item.import_status === 'ERROR' && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress variant="determinate" color="error" value={100} />
            </Box>
          )}
        </div>
      )}
      {dataToShow && documentData?.item.import_status === 'ERROR' && (
        <>
          {documentData.item.validation_errors.map((error: any) => {
            return (
              <div key={error.row}>
                <Typography variant="h5" style={{ marginTop: 10 }}>
                  Row number: <span style={{ fontWeight: 300, marginLeft: 10 }}>{error.row}</span>
                </Typography>
                <Typography variant="h5">
                  Message: <span style={{ fontWeight: 300, marginLeft: 10 }}>{error.message}</span>
                </Typography>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default DragAndDropFiles;
